"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import SoundCard from "@/components/ui/sound-card"
import { StaticSoundRow } from "@/components/ui/sound-card/static-row"
import { ErrorBoundary } from "@/components/error-boundary"
import { announce } from "@/lib/announce"
import { formatCatalogCount } from "@/lib/format-count"
import type { Sound } from "@/lib/supabase"

// Deferred slots pre-hydrate once they scroll within this margin of the
// viewport. Every deferred slot renders the same row geometry as the live
// card (StaticSoundRow), so swapping in the card never shifts layout
// regardless of the margin.
const HYDRATION_ROOT_MARGIN = "400px 0px"

interface SoundGridProps {
  sounds: Sound[]
  /** TES-1077 windowed-hydration cutoff — the first `eagerCount` cards mount
   *  on first paint; every slot past it stays a reserved-height placeholder
   *  until it scrolls near the viewport (IntersectionObserver-gated). */
  eagerCount: number
  /** §04.6 — total sounds matching the current filters (server-counted). */
  totalCount: number
  /** §04.6 — is there another batch beyond what's currently rendered. */
  hasMore: boolean
  /** §04.6 LOADING state — a batch beyond the first is in flight. */
  isLoadingMore: boolean
  /** §04.6 ERROR state — the most recent load-more attempt failed. */
  loadMoreError: string | null
  onLoadMore: () => void
  /** batch size, read from the pagination hook so the button copy never
   *  invents a number (§04.6 "State the totals honestly"). */
  batchSize: number
}

/**
 * One results-grid slot. Eager slots (above the fold) hydrate their SoundCard
 * from first paint; deferred slots render the static row (title link, eyebrow,
 * meta — no hook stack) and only mount the fully-interactive (and expensive)
 * SoundCard once they scroll within HYDRATION_ROOT_MARGIN of the viewport.
 * This windows React 19 hydration to the cards the user can actually see
 * instead of the whole page on a single post-load task — the dominant
 * residual /sounds mobile TBT cost (TES-1077) — without dropping the rows'
 * links from the server HTML. The data-sound-index wrapper is the e2e
 * hydration probe and must stay on the slot div.
 */
function CardSlot({
  sound,
  index,
  eager,
}: {
  sound: Sound
  index: number
  eager: boolean
}) {
  // SSR and the first browser render must agree: only eager slots render a
  // card. The effect schedules a deferred upgrade on browsers without
  // IntersectionObserver, so it can never remain a placeholder there.
  const [hydrated, setHydrated] = useState(eager)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (hydrated) return
    if (typeof IntersectionObserver === "undefined") {
      const fallback = window.setTimeout(() => setHydrated(true), 0)
      return () => window.clearTimeout(fallback)
    }
    const el = ref.current
    if (!el) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHydrated(true)
          observer.disconnect()
        }
      },
      { rootMargin: HYDRATION_ROOT_MARGIN }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hydrated])

  return (
    <div ref={ref} data-sound-index={index}>
      {hydrated ? (
        <ErrorBoundary>
          <SoundCard
            sound={sound}
            showCategory={true}
            keyboardShortcut={index < 9 ? index + 1 : undefined}
            disableEntryAnimation
            deferAudioUntilVisible
          />
        </ErrorBoundary>
      ) : (
        <StaticSoundRow sound={sound} showCategory={true} />
      )}
    </div>
  )
}

/**
 * Pure /sounds results grid: slot wrappers carry data-sound-index (e2e
 * hydration probe — must stay on the wrapper divs) and the bottom pagination
 * controls. All state lives in SoundsPageClient.
 */
// §04.6/§11.1 — the load-more button's aria-controls points at this id so
// screen readers know what the button affects, but the region itself is NOT
// a live region (see below): 1,400 rows each owning aria-live is the exact
// broadcast-storm §11.1 warns against — a download-count tick or a
// load-more append on ANY row becomes an announcement source outside
// lib/announce.ts's single-writer contract. The announcement is the one-line
// result count, fired once via announce() below, not a live container
// wrapping every card.
//
// Focus does NOT stay on the button "for free": disabling the currently
// focused element (`disabled={isLoadingMore}` below, needed to block a
// double-submit while the batch is in flight) forces the browser to blur it
// to <body> the instant the disabled attribute lands — spec behavior, not a
// bug in this component. Measured: document.activeElement === document.body
// at 500ms/1500ms/3500ms after a real click, with the button still in the
// DOM the whole time (btnStillInDom true, sameBtn false). The effect below
// explicitly restores focus once the batch lands and the button re-enables.
const RESULTS_LIST_ID = "sound-results-list"

export default function SoundGrid({
  sounds,
  eagerCount,
  totalCount,
  hasMore,
  isLoadingMore,
  loadMoreError,
  onLoadMore,
  batchSize,
}: SoundGridProps) {
  const gridRef = useRef<HTMLDivElement>(null)
  const loadedCount = sounds.length

  // Fix 2 (WCAG 2.2 SC 2.4.3 focus order / no keyboard trap regression):
  // restore focus to this button once a load-more batch lands. The button is
  // disabled for the duration of the fetch (below) to block a double-submit,
  // which forces the browser to blur it to <body> the instant `disabled`
  // lands — see the comment on RESULTS_LIST_ID above. loadMoreBtnRef is
  // shared by both the "Load N more" and "Try again" buttons (only one
  // renders at a time); wantsFocusRef records that THIS button initiated the
  // in-flight request, so a load triggered another way (the → keyboard
  // shortcut's onNextPage, which calls onLoadMore without ever touching this
  // button) doesn't yank focus somewhere the user never asked it to go.
  const loadMoreBtnRef = useRef<HTMLButtonElement>(null)
  const wantsFocusRef = useRef(false)
  const wasLoadingMoreRef = useRef(isLoadingMore)
  useEffect(() => {
    const wasLoading = wasLoadingMoreRef.current
    wasLoadingMoreRef.current = isLoadingMore
    if (wasLoading && !isLoadingMore && wantsFocusRef.current) {
      wantsFocusRef.current = false
      loadMoreBtnRef.current?.focus()
    }
  }, [isLoadingMore])
  const handleLoadMoreClick = useCallback(() => {
    wantsFocusRef.current = true
    onLoadMore()
  }, [onLoadMore])
  // §02.9 — every count is real, tabular, and never abbreviated.
  const positionLabel = totalCount > 0
    ? `Showing ${formatCatalogCount(loadedCount)} of ${formatCatalogCount(totalCount)}`
    : `${formatCatalogCount(loadedCount)} sound${loadedCount === 1 ? "" : "s"}`

  // §11.1 single announcement per load: fire once through the one permitted
  // live-region writer (lib/announce.ts) when loadedCount grows past the
  // first paint, not on the initial mount (that would double up with the
  // page's own first-load announcement, if any).
  const previousLoadedCountRef = useRef(loadedCount)
  useEffect(() => {
    if (loadedCount > previousLoadedCountRef.current) {
      announce(
        totalCount > 0
          ? `Showing ${formatCatalogCount(loadedCount)} of ${formatCatalogCount(totalCount)} sounds`
          : `${formatCatalogCount(loadedCount)} sound${loadedCount === 1 ? "" : "s"} loaded`,
        "polite"
      )
    }
    previousLoadedCountRef.current = loadedCount
  }, [loadedCount, totalCount])

  return (
    <>
      <div
        ref={gridRef}
        id={RESULTS_LIST_ID}
        className="grid grid-cols-1 gap-0"
      >
        {sounds.map((sound, index) => (
          <CardSlot
            key={sound.id}
            sound={sound}
            index={index}
            eager={index < eagerCount}
          />
        ))}
      </div>

      {/* §04.6 Load more — never numbered prev/next on this route; numbered
          pagination is reserved for crawlable SEO category routes. */}
      {(hasMore || loadMoreError) && (
        <div
          className="mt-8 flex flex-col items-center gap-2 border-t pt-8"
          style={{ borderColor: "var(--tls-border-subtle)" }}
        >
          {loadMoreError ? (
            <>
              <p className="text-sm font-semibold text-[var(--tls-fg-strong)]">Could not load more</p>
              <p className="max-w-xs text-center text-[0.78125rem] leading-relaxed text-[var(--tls-fg-muted)]">
                The connection dropped. The {formatCatalogCount(loadedCount)} already loaded still work.
              </p>
              <button
                ref={loadMoreBtnRef}
                type="button"
                onClick={handleLoadMoreClick}
                aria-controls={RESULTS_LIST_ID}
                className="mt-2 inline-flex min-h-11 min-w-[200px] items-center justify-center gap-2 rounded-[var(--radius-pill)] border border-[color:var(--tls-fg-strong)] px-10 text-sm font-semibold text-[var(--tls-fg-strong)] transition-colors duration-[var(--motion-duration-standard)] hover:bg-[var(--tls-surface-hover)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--tls-brand)] focus-visible:ring-offset-[3px]"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <button
                ref={loadMoreBtnRef}
                type="button"
                onClick={handleLoadMoreClick}
                disabled={isLoadingMore}
                aria-controls={RESULTS_LIST_ID}
                aria-label={isLoadingMore ? "Loading more sounds" : `Load ${batchSize} more sounds`}
                className="inline-flex min-h-11 min-w-[200px] items-center justify-center gap-2 rounded-[var(--radius-pill)] border border-[color:var(--tls-fg-strong)] px-10 text-sm font-semibold text-[var(--tls-fg-strong)] transition-colors duration-[var(--motion-duration-standard)] hover:bg-[var(--tls-surface-hover)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--tls-brand)] focus-visible:ring-offset-[3px] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Loading more sounds…
                  </>
                ) : (
                  `Load ${batchSize} more`
                )}
              </button>
              <span className="text-[0.78125rem] font-medium tabular-nums text-[var(--tls-fg-subtle)]">
                {positionLabel}
              </span>
            </>
          )}
        </div>
      )}

      {/* §04.6 EXHAUSTED — replaces the button, never a disabled button. */}
      {!hasMore && !loadMoreError && totalCount > 0 && loadedCount >= totalCount && (
        <div
          className="mt-8 flex flex-col items-center gap-1 border-t pt-8 text-center"
          style={{ borderColor: "var(--tls-border-subtle)" }}
        >
          <p className="text-[0.8125rem] leading-relaxed text-[var(--tls-fg-muted)]">
            That is all {formatCatalogCount(totalCount)}. Nothing hidden, nothing paywalled.
          </p>
        </div>
      )}
    </>
  )
}
